import { Trans, useTranslation } from 'next-i18next';
import clsx from 'clsx';

import { getSubscriptionStatus, SubscriptionTypes } from '@data/plans';

import { CheckIcon } from '@components/icons/CheckIcon';
import { StarIcon } from '@components/icons/StarIcon';
import { CrossIcon } from '@components/icons/CrossIcon';

/**
 * @param props
 * @param {typeof import('@data/plans').plans[number]} props.subscriptionPlan
 * @param {string} props.currentSubscriptionPlanType
 * @param {string} props.userEmail
 * @param {boolean} props.loading
 * @param {(checkoutLink: string) => void} props.onSubscribe
 * @param {string} [props.className]
 *
 * @returns {JSX.Element}
 */
export function SubscriptionCard({
  subscriptionPlan,
  currentSubscriptionPlanType,
  userEmail,
  loading,
  onSubscribe,
  className,
}) {
  const { t: tPlans } = useTranslation('plans');
  const { t: tt } = useTranslation('plans');

  const planStatus = getSubscriptionStatus(
    currentSubscriptionPlanType,
    subscriptionPlan.id,
  );
  const isCurrentPlan = planStatus === 'current';
  const subscribeDisabled = loading || isCurrentPlan;

  const isEssentialsPlan = subscriptionPlan.id === SubscriptionTypes.Essentials;
  const isStandardPlan = subscriptionPlan.id === SubscriptionTypes.Standard;

  /**
   * @param {'current' | 'downgrade' | 'upgrade'} planStatus
   */
  const getSubscribeButtonText = (planStatus) => {
    if (loading) {
      return tt('subscriptions.plan.subscribe.loading');
    }

    switch (planStatus) {
      case 'current':
        return tt('subscriptions.plan.subscribe.current');
      case 'downgrade':
        return tt('subscriptions.plan.subscribe.switch');
      case 'upgrade':
        return tt('subscriptions.plan.subscribe.upgrade');
      default:
        return tt('subscriptions.plan.subscribe.subscribe');
    }
  };

  return (
    <section
      className={clsx(
        '!m-0',
        'p-[24px] md:p-[32px]',
        'flex flex-col justify-between gap-[32px]',
        'border border-wtf-white/20 rounded-[12px]',
        isStandardPlan &&
          clsx('relative', 'shadow-[0_0_12px_1px] shadow-wtf-cornflowerBlue'),
        className,
      )}
    >
      <div>
        <h3
          className={clsx(
            'mt-0 mb-[16px]',
            'text-[24px] lg:text-[32px] text-wtf-white',
          )}
        >
          {tPlans(subscriptionPlan.title)}
        </h3>

        {isStandardPlan && (
          <span
            className={clsx(
              'absolute top-[12px] right-[12px]',
              'flex flex-row items-center gap-[4px]',
              'py-[6px] px-[8px]',
              'text-[16px] leading-none tracking-wide font-medium text-wtf-xanthous',
              'border-2 border-wtf-xanthous rounded-[6px]',
            )}
          >
            <StarIcon size={16} />
            Best value
          </span>
        )}

        <p className={clsx('m-0', 'text-wtf-white/60')}>
          {tPlans(subscriptionPlan.description)}
        </p>
      </div>

      <div>
        <ul
          className={clsx(
            'list-none p-0 !mt-0 !mb-[32px]',
            'flex flex-col gap-[12px]',
          )}
        >
          <li className={clsx('!m-0', 'flex flex-row items-start gap-[8px]')}>
            <CheckIcon size={28} color="#5c95ff" />

            <p
              className={clsx(
                'm-0',
                'text-[20px] text-wtf-white font-semibold',
              )}
            >
              {tt('subscriptions.messages_per_month', {
                count: subscriptionPlan.messages,
              })}
            </p>
          </li>

          <li className={clsx('!m-0', 'flex flex-row items-start gap-[8px]')}>
            <CheckIcon size={28} color="#5c95ff" />

            <p
              className={clsx(
                'm-0',
                'text-[20px] text-wtf-white font-semibold',
              )}
            >
              {tt('subscriptions.image_gen_per_month', {
                count: subscriptionPlan.images,
              })}
            </p>
          </li>
          <li className={clsx('!m-0', 'flex flex-col items-start gap-[8px]')}>
            <div className="flex flex-row items-center gap-[8px]">
              {isEssentialsPlan ? (
                <CrossIcon size={28} color="#e54242" />
              ) : (
                <CheckIcon size={28} color="#5c95ff" />
              )}
              <p
                className={clsx(
                  'm-0',
                  'text-[20px] text-wtf-white font-semibold',
                )}
              >
                {tt('subscriptions.private_gfs', {
                  girlsCount: subscriptionPlan.private_gfs,
                })}
              </p>
            </div>
            <p
              className={clsx(
                'm-0',
                'text-[20px] text-wtf-white font-semibold',
                'italic font-thin',
              )}
            ></p>
          </li>
        </ul>

        <p
          className={clsx(
            'mt-0 mb-[24px]',
            'text-[32px] leading-normal font-semibold text-wtf-white',
          )}
        >
          <Trans
            t={tt}
            i18nKey="subscriptions.price_per_month"
            values={{ price: subscriptionPlan.price }}
          >
            {'{{price}}'}{' '}
            <span className="text-[20px] text-wtf-white/60 font-normal">
              / month
            </span>
          </Trans>
        </p>

        {/*<button*/}
        {/*  type="button"*/}
        {/*  aria-disabled={subscribeDisabled}*/}
        {/*  className={clsx(*/}
        {/*    'w-full',*/}
        {/*    'flex flex-row justify-center items-center',*/}
        {/*    'py-[12px] px-[24px]',*/}
        {/*    'text-[20px] leading-none font-semibold text-wtf-white',*/}
        {/*    'bg-blue-600',*/}
        {/*    subscribeDisabled && 'cursor-not-allowed opacity-30',*/}
        {/*  )}*/}
        {/*  onClick={*/}
        {/*    subscribeDisabled*/}
        {/*      ? undefined*/}
        {/*      : () => {*/}
        {/*          const checkoutLink = subscriptionPlan.getCheckoutLink({*/}
        {/*            userEmail,*/}
        {/*          });*/}
        {/*          onSubscribe(checkoutLink);*/}
        {/*        }*/}
        {/*  }*/}
        {/*>*/}
        {/*  {getSubscribeButtonText(planStatus)}*/}
        {/*</button>*/}
      </div>
    </section>
  );
}
